const GOOGLE_DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive';
const GOOGLE_DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3/files';
const GOOGLE_DRIVE_UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3/files';

export type DriveFolder = {
  id: string;
  name: string;
};

type UploadedDriveFile = {
  id: string;
  webViewLink: string;
};

let accessToken: string | null = null;
let tokenExpiresAt = 0;
let tokenClient: google.accounts.oauth2.TokenClient | null = null;
let gisScriptPromise: Promise<void> | null = null;

declare global {
  interface Window {
    google?: typeof google;
  }
}

const USER_FACING_DRIVE_ERROR =
  'Google Drive connection is not available. Please contact support.';

function getGoogleClientId(): string {
  const clientId = import.meta.env.VITE_GOOGLE_DRIVE_CLIENT_ID?.trim();
  if (!clientId) {
    console.error(
      'Google Drive is not configured. Missing VITE_GOOGLE_DRIVE_CLIENT_ID. ' +
        'Add it to .env.local and restart the dev server (or set it in your host env for production builds).'
    );
    throw new Error(USER_FACING_DRIVE_ERROR);
  }
  return clientId;
}

function ensureGisLoaded(): Promise<void> {
  if (window.google?.accounts?.oauth2) {
    return Promise.resolve();
  }

  if (gisScriptPromise) return gisScriptPromise;

  gisScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-gis="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Identity Services.')));
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.dataset.googleGis = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services.'));
    document.head.appendChild(script);
  });

  return gisScriptPromise;
}

async function requestAccessToken(): Promise<string> {
  await ensureGisLoaded();
  const clientId = getGoogleClientId();

  return new Promise((resolve, reject) => {
    if (!window.google?.accounts?.oauth2) {
      reject(new Error('Google Identity Services unavailable.'));
      return;
    }

    if (!tokenClient) {
      tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: GOOGLE_DRIVE_SCOPE,
        callback: () => {},
      });
    }

    tokenClient.callback = (response) => {
      if (response.error || !response.access_token) {
        const detail = response.error || 'no_access_token';
        console.error('Google Drive OAuth failed:', detail, response);
        if (detail === 'access_denied' || detail === 'popup_closed_by_user') {
          reject(new Error('Google sign-in was cancelled. Please try again.'));
          return;
        }
        if (detail === 'idpiframe_initialization_failed' || detail.includes('origin')) {
          const origin = typeof window !== 'undefined' ? window.location.origin : 'your app URL';
          reject(
            new Error(
              `Google Drive is blocked for this site (origin_mismatch). In Google Cloud Console → Credentials → your Web OAuth client, add this exact Authorized JavaScript origin: ${origin} — then wait 1–2 minutes and retry. Also add http://localhost:3000, http://localhost:3001, http://127.0.0.1:3000, and http://127.0.0.1:3001 if you use local dev.`
            )
          );
          return;
        }
        reject(
          new Error(
            'Google sign-in failed. If your app is in Testing mode, add your Google account as a test user on the OAuth consent screen.'
          )
        );
        return;
      }
      accessToken = response.access_token;
      tokenExpiresAt = Date.now() + (response.expires_in ?? 3600) * 1000;
      resolve(response.access_token);
    };

    tokenClient.requestAccessToken({
      prompt: accessToken ? '' : 'consent',
    });
  });
}

async function getValidAccessToken(): Promise<string> {
  if (accessToken && Date.now() < tokenExpiresAt - 30000) {
    return accessToken;
  }
  return requestAccessToken();
}

async function driveRequest<T>(url: string, init: RequestInit = {}): Promise<T> {
  const token = await getValidAccessToken();
  const headers = new Headers(init.headers || {});
  headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(url, { ...init, headers });
  if (!response.ok) {
    throw new Error('Google Drive request failed.');
  }
  return response.json() as Promise<T>;
}

/** Find a folder by exact name under a parent (Drive files.list). */
async function findFolderByName(name: string, parentId: string): Promise<DriveFolder | null> {
  const safeName = name.replace(/'/g, "\\'");
  const query = encodeURIComponent(
    `'${parentId}' in parents and name='${safeName}' and mimeType='application/vnd.google-apps.folder' and trashed=false`
  );
  const url = `${GOOGLE_DRIVE_API_BASE}?q=${query}&fields=files(id,name)&pageSize=1`;
  const data = await driveRequest<{ files: DriveFolder[] }>(url);
  return data.files[0] ?? null;
}

/** Create a folder under a parent (Drive files.create). */
async function createFolder(name: string, parentId: string): Promise<DriveFolder> {
  return driveRequest<DriveFolder>(`${GOOGLE_DRIVE_API_BASE}?fields=id,name`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      mimeType: 'application/vnd.google-apps.folder',
      parents: [parentId],
    }),
  });
}

async function uploadFileToFolder(
  fileContent: Blob,
  fileName: string,
  folderId: string
): Promise<UploadedDriveFile> {
  const metadata = {
    name: fileName,
    parents: [folderId],
  };

  const formData = new FormData();
  formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  formData.append('file', fileContent, fileName);

  return driveRequest<UploadedDriveFile>(
    `${GOOGLE_DRIVE_UPLOAD_BASE}?uploadType=multipart&fields=id,webViewLink`,
    {
      method: 'POST',
      body: formData,
    }
  );
}

/** Ensure Google auth is ready (e.g. before opening the folder browser). */
export async function ensureGoogleDriveAuth(): Promise<void> {
  await getValidAccessToken();
}

/** List folders under a parent (or My Drive root when parentId is omitted / 'root'). */
export async function listDriveFolders(parentId: string = 'root'): Promise<DriveFolder[]> {
  await getValidAccessToken();
  const parentFilter = parentId === 'root' ? `'root' in parents` : `'${parentId}' in parents`;
  const query = encodeURIComponent(
    `mimeType='application/vnd.google-apps.folder' and trashed=false and ${parentFilter}`
  );
  const url = `${GOOGLE_DRIVE_API_BASE}?q=${query}&fields=files(id,name)&orderBy=name&pageSize=100`;
  const data = await driveRequest<{ files: DriveFolder[] }>(url);
  return data.files ?? [];
}

/**
 * Upload a redacted file under parentFolder / studentCode /.
 * Creates the student subfolder if it does not already exist.
 */
export async function uploadRedactedFileToGoogleDrive(params: {
  studentCode: string;
  fileName: string;
  fileBlob: Blob;
  parentFolderId: string;
  parentFolderName: string;
}): Promise<{ fileId: string; webViewLink: string; folderPath: string; createdStudentFolder: boolean }> {
  await getValidAccessToken();
  const existing = await findFolderByName(params.studentCode, params.parentFolderId);
  const studentFolder = existing ?? (await createFolder(params.studentCode, params.parentFolderId));
  const uploaded = await uploadFileToFolder(params.fileBlob, params.fileName, studentFolder.id);
  const folderPath = `${params.parentFolderName} / ${params.studentCode} /`;

  return {
    fileId: uploaded.id,
    webViewLink: uploaded.webViewLink || `https://drive.google.com/file/d/${uploaded.id}/view`,
    folderPath,
    createdStudentFolder: !existing,
  };
}
